import { DataGrid, GridFooterContainer } from '@mui/x-data-grid';
import { useMemo } from 'react';
import Pagination from '../Pagination';

const DataTableFooter = ({ page, limit, total, onPageChange, loading }) => (
  <GridFooterContainer sx={{ justifyContent: 'end', minHeight: 40 }}>
    <Pagination
      page={page}
      limit={limit}
      total={total}
      onPageChange={onPageChange}
      loading={loading}
    />
  </GridFooterContainer>
);

const DataTable = ({
  columns,
  items = [],
  rowKey,
  onRowClick,
  page,
  limit,
  total,
  onPageChange,
  loading = false,
  wide = false,
  selectedRowKey,
}) => {
  const rows = useMemo(
    () =>
      items.map((item, index) => ({
        ...item,
        id: rowKey(item, index),
        sourceItem: item,
      })),
    [items, rowKey],
  );

  const gridColumns = useMemo(
    () =>
      columns.map((column) => ({
        field: column.key,
        headerName: column.label,
        minWidth: column.minWidth || (wide ? 170 : 120),
        width: column.width,
        flex: column.width ? undefined : 1,
        sortable: false,
        filterable: false,
        renderCell: column.render ? (params) => column.render(params.row.sourceItem) : undefined,
      })),
    [columns, wide],
  );

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <DataGrid
      rows={rows}
      columns={gridColumns}
      rowHeight={40}
      columnHeaderHeight={38}
      loading={loading}
      hideFooter={totalPages <= 1}
      slots={{ footer: DataTableFooter }}
      slotProps={{
        footer: {
          page,
          limit,
          total,
          onPageChange,
          loading,
        },
      }}
      disableColumnMenu
      disableRowSelectionOnClick
      onRowClick={onRowClick ? (params) => onRowClick(params.row.sourceItem) : undefined}
      getRowClassName={(params) =>
        selectedRowKey != null && params.id === selectedRowKey ? 'selected-row' : ''
      }
      localeText={{
        noRowsLabel: 'אין נתונים להצגה',
        noResultsOverlayLabel: 'אין תוצאות להצגה',
      }}
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: 0,
        flex: 1,
        border: 1,
        borderColor: '#b1b1b1',
        fontSize: 12.5,

        '& .MuiDataGrid-columnHeader': {
          backgroundColor: '#ebebeb',
          fontSize: 12,
          fontWeight: 900,
        },

        '& .MuiDataGrid-scrollbarFiller': {
          backgroundColor: '#cfd0d1',
        },

        '& .MuiDataGrid-filler': {
          backgroundColor: '#f1f1f1',
        },

        '& .MuiDataGrid-cell': {
          alignItems: 'center',
          textAlign: 'right',
        },

        '& .MuiDataGrid-row': {
          cursor: onRowClick ? 'pointer' : 'default',
        },

        '& .MuiDataGrid-row:hover': {
          backgroundColor: '#eef8f7',
        },

        '& .MuiDataGrid-row.selected-row': {
          backgroundColor: '#ddebff',
          boxShadow: 'inset -4px 0 0 #1677ff',
        },

        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },

        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        },
      }}
    />
  );
};

export default DataTable;
